import {
  SoftwareCatalogEntity,
  EngineeringTeam,
  TechnologyDependency,
  ApplicationModuleDefinition,
  GoldenPathDefinition,
  GoldenPathExecution,
  PlatformServiceOffering,
  PlatformProvisioningRequest,
  APIRegistryEntry,
  EventDefinition,
  IntegrationDefinition,
  ArchitectureDecisionRecord,
  TechnologyRadarItem,
  EngineeringStandard,
  EngineeringScorecard,
  TechnicalDebtItem,
  ArchitectureRisk,
  TechnologyCostItem,
  TechnologyUnitEconomics,
  DORAMetricsSummary,
  PlatformHealthIndicator,
  ArchitectureImpactAnalysis
} from './platformTypes';

// 1. Engineering Teams
export const MOCK_TEAMS: EngineeringTeam[] = [
  {
    id: 'team-core-fin',
    name: 'Financial Intelligence Core Team',
    lead: 'Dr. Evelyn Vance (Chief Actuary / Software Architect)',
    slackChannel: '#eng-financial-core',
    onCallRotation: 'PagerDuty: On-Call Financial Engines Primary',
    email: 'fin-core@yamoopay.internal',
    busFactorScore: 4,
    membersCount: 8
  },
  {
    id: 'team-platform-sre',
    name: 'Platform Engineering & SRE',
    lead: 'Alexandre Renard (Principal Platform Architect)',
    slackChannel: '#eng-platform-ops',
    onCallRotation: 'PagerDuty: Production SRE & Dokploy Ops',
    email: 'platform-sre@yamoopay.internal',
    busFactorScore: 3,
    membersCount: 6
  },
  {
    id: 'team-sec-id',
    name: 'Security & Identity Engineering',
    lead: 'Kavita Sundaram (Principal Security Architect)',
    slackChannel: '#eng-security-zero-trust',
    onCallRotation: 'PagerDuty: SecOps & Auth Incidents',
    email: 'sec-identity@yamoopay.internal',
    busFactorScore: 3,
    membersCount: 5
  },
  {
    id: 'team-billing-growth',
    name: 'Billing, Growth & Monetization',
    lead: 'Marcus Thorne (Lead Fullstack Architect)',
    slackChannel: '#eng-monetization-billing',
    onCallRotation: 'PagerDuty: Stripe & Billing Webhooks',
    email: 'billing-growth@yamoopay.internal',
    busFactorScore: 3,
    membersCount: 7
  },
  {
    id: 'team-developer-ecosystem',
    name: 'Developer Platform & Integrations',
    lead: 'Sébastien Mercier (Principal API Architect)',
    slackChannel: '#eng-developer-platform',
    onCallRotation: 'PagerDuty: Public Gateway & Webhook Dispatcher',
    email: 'api-platform@yamoopay.internal',
    busFactorScore: 2, // Highlighted minor bus factor risk
    membersCount: 4
  }
];

// 2. Software Catalog Entities
export const MOCK_CATALOG_ENTITIES: SoftwareCatalogEntity[] = [
  {
    entityId: 'srv-fin-salary-engine',
    kind: 'MODULE',
    name: 'Salary & Multi-Country Tax Engine',
    description: 'Deterministic gross-to-net calculation core supporting 25+ statutory jurisdictions (FR, UK, US, DE, CA, SN, CI, etc.)',
    domain: 'Financial Intelligence',
    system: 'Calculators & Tax Domain',
    lifecycle: 'PRODUCTION',
    criticality: 'TIER_0',
    ownership: {
      ownerTeamId: 'team-core-fin',
      ownerTeamName: 'Financial Intelligence Core Team',
      technicalLead: 'Dr. Evelyn Vance',
      businessOwner: 'Chief Actuary / Product Lead',
      secondaryLead: 'Thomas Laurent',
      ownershipType: 'PRIMARY',
      onCallSchedule: 'PagerDuty: Financial Engine On-Call',
      codeownersPath: '/src/services/salaryEngine.ts'
    },
    repository: {
      url: 'https://github.com/yamoopay/yamoopay-monorepo',
      monorepoPath: 'src/services/salaryEngine.ts',
      defaultBranch: 'main'
    },
    documentation: {
      readmeUrl: 'https://docs.yamoopay.internal/modules/salary-engine',
      architectureDocUrl: 'https://docs.yamoopay.internal/arch/ADR-PLAT-002',
      runbookUrl: 'https://runbooks.yamoopay.internal/rb-fin-salary-calc-001',
      lastReviewedAt: '2026-08-15'
    },
    runtime: {
      language: 'TypeScript 5.8',
      framework: 'React / Node.js Engine',
      nodeVersion: 'v22.14 LTS',
      deploymentUnit: 'Docker Container / Dokploy on Hostinger VPS (kvm8)',
      environment: 'PRODUCTION',
      port: 3000
    },
    slo: {
      availabilityTarget: '99.99%',
      latencyP99Target: '< 45ms',
      errorBudgetCurrent: '98.5% remaining',
      sliMetricName: 'yamoopay.calc.salary.duration_ms'
    },
    dataClassification: 'INTERNAL',
    costCenter: 'CC-FIN-CORE-01',
    allocatedMonthlyCostUsd: 145.00,
    createdAt: '2025-01-10',
    lastVerifiedAt: '2026-08-20',
    dependenciesCount: { upstream: 2, downstream: 14 },
    scorecardStatus: 'PASS'
  },
  {
    entityId: 'srv-fin-fx-intelligence',
    kind: 'MODULE',
    name: 'Currency & Real-Time FX Rate Stream',
    description: 'High-precision FX conversion engine with European Central Bank (ECB), West African BCEAO, and multi-broker liquidity feeds',
    domain: 'Currency Intelligence',
    system: 'Market Data & FX',
    lifecycle: 'PRODUCTION',
    criticality: 'TIER_0',
    ownership: {
      ownerTeamId: 'team-core-fin',
      ownerTeamName: 'Financial Intelligence Core Team',
      technicalLead: 'Dr. Evelyn Vance',
      businessOwner: 'VP Financial Markets',
      ownershipType: 'PRIMARY',
      onCallSchedule: 'PagerDuty: Financial Engine On-Call',
      codeownersPath: '/src/services/fxEngine.ts'
    },
    repository: {
      url: 'https://github.com/yamoopay/yamoopay-monorepo',
      monorepoPath: 'src/services/fxEngine.ts',
      defaultBranch: 'main'
    },
    documentation: {
      readmeUrl: 'https://docs.yamoopay.internal/modules/fx-engine',
      runbookUrl: 'https://runbooks.yamoopay.internal/rb-fx-rates-sync-002',
      lastReviewedAt: '2026-08-12'
    },
    runtime: {
      language: 'TypeScript',
      framework: 'In-Memory Stream & Redis Cache Layer',
      deploymentUnit: 'Docker Container / Dokploy (kvm8-vps-main)',
      environment: 'PRODUCTION',
      port: 3000
    },
    slo: {
      availabilityTarget: '99.99%',
      latencyP99Target: '< 20ms',
      errorBudgetCurrent: '99.1% remaining',
      sliMetricName: 'yamoopay.fx.rate_latency_ms'
    },
    dataClassification: 'PUBLIC',
    costCenter: 'CC-FIN-CORE-01',
    allocatedMonthlyCostUsd: 85.00,
    createdAt: '2025-01-05',
    lastVerifiedAt: '2026-08-24',
    dependenciesCount: { upstream: 3, downstream: 19 },
    scorecardStatus: 'PASS'
  },
  {
    entityId: 'srv-sec-zero-trust-auth',
    kind: 'MODULE',
    name: 'Zero-Trust Identity & Session Authority',
    description: 'Stateless JWT + Passkey/WebAuthn identity gate, RBAC enforcement, session revocation & device fingerprinting',
    domain: 'Security & Identity',
    system: 'Security Architecture',
    lifecycle: 'PRODUCTION',
    criticality: 'TIER_0',
    ownership: {
      ownerTeamId: 'team-sec-id',
      ownerTeamName: 'Security & Identity Engineering',
      technicalLead: 'Kavita Sundaram',
      businessOwner: 'Chief Information Security Officer (CISO)',
      ownershipType: 'PRIMARY',
      onCallSchedule: 'PagerDuty: SecOps Critical Auth',
      codeownersPath: '/src/services/security/'
    },
    repository: {
      url: 'https://github.com/yamoopay/yamoopay-monorepo',
      monorepoPath: 'src/services/security',
      defaultBranch: 'main'
    },
    documentation: {
      readmeUrl: 'https://docs.yamoopay.internal/security/zero-trust-architecture',
      runbookUrl: 'https://runbooks.yamoopay.internal/rb-auth-session-compromise-009',
      lastReviewedAt: '2026-08-18'
    },
    runtime: {
      language: 'TypeScript',
      framework: 'Express Security Middleware / Crypto Web API',
      deploymentUnit: 'Docker Container / Dokploy (kvm8-vps-main)',
      environment: 'PRODUCTION',
      port: 3000
    },
    slo: {
      availabilityTarget: '99.99%',
      latencyP99Target: '< 15ms',
      errorBudgetCurrent: '96.8% remaining',
      sliMetricName: 'yamoopay.auth.token_verify_duration_ms'
    },
    dataClassification: 'RESTRICTED_FINANCIAL_PII',
    costCenter: 'CC-SEC-01',
    allocatedMonthlyCostUsd: 110.00,
    createdAt: '2025-01-15',
    lastVerifiedAt: '2026-08-22',
    dependenciesCount: { upstream: 2, downstream: 28 },
    scorecardStatus: 'PASS'
  },
  {
    entityId: 'srv-db-postgres-primary',
    kind: 'DATABASE',
    name: 'PostgreSQL Primary System of Record',
    description: 'PostgreSQL 17 ACID database on NVMe storage storing users, calculations, subscriptions, and audit logs with WAL backups',
    domain: 'Data Platform',
    system: 'Infrastructure Tier',
    lifecycle: 'PRODUCTION',
    criticality: 'TIER_0',
    ownership: {
      ownerTeamId: 'team-platform-sre',
      ownerTeamName: 'Platform Engineering & SRE',
      technicalLead: 'Alexandre Renard',
      businessOwner: 'Chief Technology Officer (CTO)',
      ownershipType: 'PRIMARY',
      onCallSchedule: 'PagerDuty: DBA & Storage Escalation',
      codeownersPath: '/docker-compose.yml'
    },
    repository: {
      url: 'https://github.com/yamoopay/yamoopay-monorepo',
      monorepoPath: 'infrastructure/db',
      defaultBranch: 'main'
    },
    documentation: {
      readmeUrl: 'https://docs.yamoopay.internal/data/postgresql-schema',
      runbookUrl: 'https://runbooks.yamoopay.internal/rb-db-failover-wal-restore-003',
      lastReviewedAt: '2026-08-10'
    },
    runtime: {
      language: 'SQL / C',
      framework: 'PostgreSQL 17.2 with pgvector & pgcrypto',
      deploymentUnit: 'Dedicated Docker Volume on Hostinger VPS NVMe',
      environment: 'PRODUCTION',
      port: 5432
    },
    slo: {
      availabilityTarget: '99.99%',
      latencyP99Target: '< 25ms query latency',
      errorBudgetCurrent: '99.8% remaining',
      sliMetricName: 'postgresql.query.p99_duration_ms'
    },
    dataClassification: 'RESTRICTED_FINANCIAL_PII',
    costCenter: 'CC-INFRA-01',
    allocatedMonthlyCostUsd: 220.00,
    createdAt: '2025-01-01',
    lastVerifiedAt: '2026-08-25',
    dependenciesCount: { upstream: 0, downstream: 12 },
    scorecardStatus: 'PASS'
  },
  {
    entityId: 'srv-api-public-gateway',
    kind: 'API',
    name: 'YamooPay Developer Public API Gateway',
    description: 'Public REST & Webhook Gateway with Tiered Rate Limiting (Token Bucket), Idempotency Keys, and OpenAPI 3.1 contracts',
    domain: 'Developer Platform',
    system: 'API Platform',
    lifecycle: 'PRODUCTION',
    criticality: 'TIER_1',
    ownership: {
      ownerTeamId: 'team-developer-ecosystem',
      ownerTeamName: 'Developer Platform & Integrations',
      technicalLead: 'Sébastien Mercier',
      businessOwner: 'VP Developer Relations',
      ownershipType: 'PRIMARY',
      onCallSchedule: 'PagerDuty: Public Gateway Tier 1',
      codeownersPath: '/src/services/developer/'
    },
    repository: {
      url: 'https://github.com/yamoopay/yamoopay-monorepo',
      monorepoPath: 'src/services/developer',
      defaultBranch: 'main'
    },
    documentation: {
      readmeUrl: 'https://docs.yamoopay.com/api-reference',
      runbookUrl: 'https://runbooks.yamoopay.internal/rb-api-gateway-rate-limit-spike',
      lastReviewedAt: '2026-08-21'
    },
    runtime: {
      language: 'TypeScript',
      framework: 'Express Router + OpenAPI 3.1 Spec Generator',
      deploymentUnit: 'Docker Container / Dokploy',
      environment: 'PRODUCTION',
      port: 3000
    },
    slo: {
      availabilityTarget: '99.95%',
      latencyP99Target: '< 180ms',
      errorBudgetCurrent: '91.4% remaining',
      sliMetricName: 'yamoopay.api.gateway.p99_latency_ms'
    },
    dataClassification: 'PUBLIC',
    costCenter: 'CC-DEV-ECO-01',
    allocatedMonthlyCostUsd: 130.00,
    createdAt: '2025-03-01',
    lastVerifiedAt: '2026-08-23',
    dependenciesCount: { upstream: 6, downstream: 4 },
    scorecardStatus: 'PASS'
  },
  {
    entityId: 'srv-wrk-fx-sync-job',
    kind: 'SCHEDULED_JOB',
    name: 'ECB & Central Bank Rate Ingestion Worker',
    description: 'Automated 15-minute cron job fetching and validating currency rates against statutory bands with circuit-breaker protection',
    domain: 'Currency Intelligence',
    system: 'Background Workers',
    lifecycle: 'PRODUCTION',
    criticality: 'TIER_1',
    ownership: {
      ownerTeamId: 'team-core-fin',
      ownerTeamName: 'Financial Intelligence Core Team',
      technicalLead: 'Thomas Laurent',
      businessOwner: 'VP Financial Markets',
      ownershipType: 'PRIMARY',
      onCallSchedule: 'PagerDuty: Financial Engine On-Call',
      codeownersPath: '/src/services/fxEngine.ts'
    },
    repository: {
      url: 'https://github.com/yamoopay/yamoopay-monorepo',
      monorepoPath: 'workers/fx-sync',
      defaultBranch: 'main'
    },
    documentation: {
      readmeUrl: 'https://docs.yamoopay.internal/workers/fx-sync',
      runbookUrl: 'https://runbooks.yamoopay.internal/rb-wrk-fx-sync-stale-feed-004',
      lastReviewedAt: '2026-08-14'
    },
    runtime: {
      language: 'TypeScript / Node.js Worker',
      framework: 'BullMQ / Redis Backed Cron',
      deploymentUnit: 'Background Worker inside Docker Container',
      environment: 'PRODUCTION'
    },
    slo: {
      availabilityTarget: '99.9%',
      latencyP99Target: '< 4.5s job execution',
      errorBudgetCurrent: '97.0% remaining',
      sliMetricName: 'yamoopay.worker.fx_sync.success_rate'
    },
    dataClassification: 'PUBLIC',
    costCenter: 'CC-FIN-CORE-01',
    allocatedMonthlyCostUsd: 40.00,
    createdAt: '2025-02-10',
    lastVerifiedAt: '2026-08-24',
    dependenciesCount: { upstream: 3, downstream: 1 },
    scorecardStatus: 'PASS'
  }
];

// 3. Application Module Boundaries (Modular Monolith Governance)
export const MOCK_APPLICATION_MODULES: ApplicationModuleDefinition[] = [
  {
    moduleId: 'mod-salary-payroll',
    name: 'Salary & Payroll Module',
    domain: 'Financial Intelligence',
    ownerTeam: 'team-core-fin',
    publicInterfaceExport: 'export { calculateGrossToNet, parseTaxBracket, validateActuarialRules } from "./salaryEngine"',
    databaseTablesOwned: ['payroll_tax_brackets', 'statutory_deductions', 'salary_calculation_snapshots'],
    permittedInboundModules: ['mod-public-api', 'mod-web-frontend', 'mod-universal-calculators', 'mod-quality-suite'],
    prohibitedInboundModules: ['mod-ad-monetization', 'mod-raw-database-direct'],
    eventsEmitted: ['salary.calculation_executed', 'salary.statutory_ruleset_updated'],
    eventsConsumed: ['currency.rates_updated'],
    lifecycle: 'PRODUCTION',
    isolationScore: 96
  },
  {
    moduleId: 'mod-fx-rates',
    name: 'Currency & Exchange Rate Module',
    domain: 'Currency Intelligence',
    ownerTeam: 'team-core-fin',
    publicInterfaceExport: 'export { convertCurrency, getLiveRate, calculateProviderMargin } from "./fxEngine"',
    databaseTablesOwned: ['fx_historical_rates', 'fx_spread_matrices', 'central_bank_statutory_rates'],
    permittedInboundModules: ['mod-salary-payroll', 'mod-money-transfer', 'mod-public-api', 'mod-web-frontend'],
    prohibitedInboundModules: ['mod-ad-monetization'],
    eventsEmitted: ['currency.rates_updated', 'currency.volatility_spike_detected'],
    eventsConsumed: ['worker.ecb_sync_completed'],
    lifecycle: 'PRODUCTION',
    isolationScore: 98
  },
  {
    moduleId: 'mod-identity-security',
    name: 'Identity & Zero-Trust Auth Module',
    domain: 'Security & Identity',
    ownerTeam: 'team-sec-id',
    publicInterfaceExport: 'export { verifySessionJwt, enforceRbac, generateWebAuthnChallenge } from "./security"',
    databaseTablesOwned: ['users', 'user_sessions', 'webauthn_credentials', 'rbac_permissions', 'security_audit_logs'],
    permittedInboundModules: ['*'], // Auth gate serves all
    prohibitedInboundModules: [],
    eventsEmitted: ['identity.user_logged_in', 'identity.suspicious_ip_blocked', 'identity.password_reset'],
    eventsConsumed: [],
    lifecycle: 'PRODUCTION',
    isolationScore: 100
  },
  {
    moduleId: 'mod-billing-stripe',
    name: 'Billing, Invoicing & VAT Subscriptions',
    domain: 'Billing & Monetization',
    ownerTeam: 'team-billing-growth',
    publicInterfaceExport: 'export { createSubscriptionCheckout, verifyVatNumber, processStripeWebhook } from "./billing"',
    databaseTablesOwned: ['billing_customers', 'subscriptions', 'invoices', 'vat_validations', 'credit_notes'],
    permittedInboundModules: ['mod-public-api', 'mod-web-frontend', 'mod-cxo-support'],
    prohibitedInboundModules: ['mod-salary-payroll', 'mod-fx-rates'],
    eventsEmitted: ['billing.subscription_activated', 'billing.payment_succeeded', 'billing.invoice_issued'],
    eventsConsumed: ['identity.user_registered'],
    lifecycle: 'PRODUCTION',
    isolationScore: 94
  }
];

// 4. Technology Dependencies & Graph
export const MOCK_DEPENDENCIES: TechnologyDependency[] = [
  {
    id: 'dep-001',
    sourceEntityId: 'srv-fin-salary-engine',
    sourceName: 'Salary Engine',
    targetEntityId: 'srv-fin-fx-intelligence',
    targetName: 'FX Rate Engine',
    type: 'CALLS',
    criticality: 'TIER_0',
    sourceOfEvidence: 'DECLARED',
    confidence: 1.0,
    environment: 'PRODUCTION',
    isSynchronous: true,
    isSinglePointOfFailure: false,
    lastObservedAt: '2026-08-25 11:20:00'
  },
  {
    id: 'dep-002',
    sourceEntityId: 'srv-fin-salary-engine',
    sourceName: 'Salary Engine',
    targetEntityId: 'srv-db-postgres-primary',
    targetName: 'PostgreSQL Primary',
    type: 'READS_FROM',
    criticality: 'TIER_0',
    sourceOfEvidence: 'OBSERVED',
    confidence: 1.0,
    environment: 'PRODUCTION',
    isSynchronous: true,
    isSinglePointOfFailure: true,
    lastObservedAt: '2026-08-25 11:22:15'
  },
  {
    id: 'dep-003',
    sourceEntityId: 'srv-api-public-gateway',
    sourceName: 'Developer Public API Gateway',
    targetEntityId: 'srv-sec-zero-trust-auth',
    targetName: 'Zero-Trust Identity Module',
    type: 'AUTHENTICATES_WITH',
    criticality: 'TIER_0',
    sourceOfEvidence: 'OBSERVED',
    confidence: 1.0,
    environment: 'PRODUCTION',
    isSynchronous: true,
    isSinglePointOfFailure: true,
    lastObservedAt: '2026-08-25 11:21:00'
  },
  {
    id: 'dep-004',
    sourceEntityId: 'srv-api-public-gateway',
    sourceName: 'Developer Public API Gateway',
    targetEntityId: 'srv-fin-salary-engine',
    targetName: 'Salary Engine',
    type: 'CALLS',
    criticality: 'TIER_1',
    sourceOfEvidence: 'DECLARED',
    confidence: 1.0,
    environment: 'PRODUCTION',
    isSynchronous: true,
    isSinglePointOfFailure: false,
    lastObservedAt: '2026-08-25 11:19:30'
  },
  {
    id: 'dep-005',
    sourceEntityId: 'srv-wrk-fx-sync-job',
    sourceName: 'ECB Rate Ingestion Worker',
    targetEntityId: 'srv-db-postgres-primary',
    targetName: 'PostgreSQL Primary',
    type: 'WRITES_TO',
    criticality: 'TIER_1',
    sourceOfEvidence: 'OBSERVED',
    confidence: 1.0,
    environment: 'PRODUCTION',
    isSynchronous: true,
    isSinglePointOfFailure: false,
    lastObservedAt: '2026-08-25 11:15:00'
  }
];

// 5. Golden Paths
export const MOCK_GOLDEN_PATHS: GoldenPathDefinition[] = [
  {
    id: 'gp-new-module',
    name: 'Create New Domain Module in Monolith',
    goal: 'Scaffold a strictly typed, isolated modular TypeScript domain with unit tests, catalog metadata, and zero circular dependencies',
    targetAudience: 'Product & Core Engineers',
    steps: [
      { order: 1, title: 'Define Domain Boundary & Schema', description: 'Declare module boundary, domain ownership, and database table mapping', automated: true, estimatedMinutes: 2 },
      { order: 2, title: 'Generate TypeScript Interfaces & Tests', description: 'Scaffold typed public contract, index.ts export, and Vitest test suite', automated: true, estimatedMinutes: 3 },
      { order: 3, title: 'Register in Software Catalog (catalog.yaml)', description: 'Auto-register entity metadata, owner team, SLO, and data classification', automated: true, estimatedMinutes: 1 },
      { order: 4, title: 'CI Architectural Verification Gate', description: 'Run dependency linting to ensure no illegal imports or circular chains', automated: true, estimatedMinutes: 2 }
    ],
    guardrails: [
      'Prohibited from importing database models of another domain directly',
      'Must expose all capabilities via a single index.ts public interface',
      'Must include minimum 90% branch test coverage before merge'
    ],
    owner: 'Platform Engineering Team',
    version: '2.4.0',
    adoptionPercentage: 94.5,
    averageExecutionMinutes: 8,
    successRate: 98.2
  },
  {
    id: 'gp-new-worker',
    name: 'Create Background Worker / Cron Job',
    goal: 'Create an idempotent BullMQ/Redis worker on Hostinger VPS with structured error handling, dead-letter queue, and Prometheus metrics',
    targetAudience: 'Backend & Data Engineers',
    steps: [
      { order: 1, title: 'Declare Job Contract & Payload Schema', description: 'Define TypeScript job data with JSON Schema validation and idempotency key', automated: true, estimatedMinutes: 2 },
      { order: 2, title: 'Scaffold Worker Process & DLQ Handler', description: 'Inject retry exponential backoff, circuit breaker, and DLQ alerting', automated: true, estimatedMinutes: 3 },
      { order: 3, title: 'Attach OpenTelemetry Trace Context', description: 'Ensure trace context propagation across job enqueue and execution', automated: true, estimatedMinutes: 1 },
      { order: 4, title: 'Register Job in SRE Runbook Registry', description: 'Generate standard Markdown runbook with failure diagnostics', automated: true, estimatedMinutes: 2 }
    ],
    guardrails: [
      'Must be strictly idempotent (safe to retry multiple times without side effects)',
      'Must never process raw credit card numbers or unencrypted secrets in payload',
      'Max execution duration bounded to 300 seconds'
    ],
    owner: 'Platform Engineering & SRE',
    version: '1.8.1',
    adoptionPercentage: 91.0,
    averageExecutionMinutes: 8,
    successRate: 96.5
  },
  {
    id: 'gp-new-financial-engine',
    name: 'New Statutory Financial Calculator / Engine',
    goal: 'Implement a certified actuarial calculation engine with golden test fixtures, jurisdiction tax laws, and CQO verification',
    targetAudience: 'Financial Core Engineers & Actuaries',
    steps: [
      { order: 1, title: 'Statutory Law & Formula Specification', description: 'Document official government legislation reference and precision rules', automated: false, estimatedMinutes: 15 },
      { order: 2, title: 'Actuarial Formula Implementation', description: 'Implement pure deterministic TypeScript functions with BigNumber/fixed decimal', automated: false, estimatedMinutes: 30 },
      { order: 3, title: 'Golden Test Fixture Automation', description: 'Generate 100+ edge-case test matrices (brackets, exemptions, cross-border)', automated: true, estimatedMinutes: 5 },
      { order: 4, title: 'Release Certification Gate (Prompt 23)', description: 'Validate zero-regression delta against official tax authority calculators', automated: true, estimatedMinutes: 4 }
    ],
    guardrails: [
      'Zero floating-point arithmetic errors (must use fixed-point scale integer math)',
      'Statutory ruleset must have versioned effective dates and jurisdictions',
      'Mandatory peer review by Chief Actuary or CQO delegate'
    ],
    owner: 'Financial Intelligence Core Team',
    version: '3.1.0',
    adoptionPercentage: 100.0,
    averageExecutionMinutes: 54,
    successRate: 99.4
  }
];

// 6. Platform Self-Service Offerings
export const MOCK_PLATFORM_OFFERINGS: PlatformServiceOffering[] = [
  {
    id: 'offering-redis-namespace',
    name: 'Redis Isolated Cache Namespace',
    category: 'DATASTORE',
    description: 'Provision a memory-bounded, TTL-enforced Redis key namespace on the shared Redis instance with eviction guardrails',
    ownerTeam: 'team-platform-sre',
    inputsSchema: [
      { field: 'namespaceName', type: 'string', required: true, description: 'Prefix for keys (e.g. fx_rate_cache:)' },
      { field: 'defaultTtlSeconds', type: 'number', required: true, description: 'Default expiration in seconds (max 86400)' },
      { field: 'memoryBudgetMb', type: 'number', required: true, description: 'Estimated RAM consumption in MB (max 256MB)' }
    ],
    approvalRequired: false,
    provisioningSlaMinutes: 1,
    costImpactUsdPerMonth: 5.00,
    status: 'ACTIVE'
  },
  {
    id: 'offering-postgres-schema',
    name: 'PostgreSQL Isolated Domain Schema',
    category: 'DATASTORE',
    description: 'Create a dedicated PostgreSQL database schema with role-based access, automated daily backup retention, and Drizzle migration directory',
    ownerTeam: 'team-platform-sre',
    inputsSchema: [
      { field: 'schemaName', type: 'string', required: true, description: 'Name of the schema (e.g. payroll_tax_v2)' },
      { field: 'ownerTeam', type: 'enum', required: true, options: ['Financial Core', 'Security', 'Billing', 'Developer Platform'], description: 'Owning engineering team' },
      { field: 'dataClassification', type: 'enum', required: true, options: ['PUBLIC', 'INTERNAL', 'RESTRICTED_PII'], description: 'Data privacy sensitivity level' }
    ],
    approvalRequired: true,
    provisioningSlaMinutes: 10,
    costImpactUsdPerMonth: 15.00,
    status: 'ACTIVE'
  },
  {
    id: 'offering-bullmq-queue',
    name: 'BullMQ Asynchronous Work Queue',
    category: 'MESSAGING',
    description: 'Set up an asynchronous worker queue with Redis streaming, Prometheus latency metrics, dead-letter queue, and retry policies',
    ownerTeam: 'team-platform-sre',
    inputsSchema: [
      { field: 'queueName', type: 'string', required: true, description: 'Unique queue identifier (e.g. email_dispatch_queue)' },
      { field: 'concurrency', type: 'number', required: true, description: 'Concurrent worker threads (1-10)' },
      { field: 'maxRetries', type: 'number', required: true, description: 'Maximum retry attempts on transient failure' }
    ],
    approvalRequired: false,
    provisioningSlaMinutes: 2,
    costImpactUsdPerMonth: 8.00,
    status: 'ACTIVE'
  }
];

export const MOCK_PROVISIONING_REQUESTS: PlatformProvisioningRequest[] = [
  {
    id: 'req-prov-1082',
    offeringId: 'offering-redis-namespace',
    offeringName: 'Redis Isolated Cache Namespace',
    requestedBy: 'Thomas Laurent',
    requestingTeam: 'Financial Intelligence Core Team',
    environment: 'PRODUCTION',
    inputs: { namespaceName: 'vat_lookup_cache:', defaultTtlSeconds: 3600, memoryBudgetMb: 64 },
    state: 'SUCCEEDED',
    dryRunPlan: [
      'Validate key prefix collision against active namespaces',
      'Allocate 64MB memory quota in Redis config',
      'Create Prometheus metric tag `redis_namespace_vat_lookup`'
    ],
    createdAt: '2026-08-24 09:14:00',
    resolvedAt: '2026-08-24 09:14:45',
    resourceCreatedArnOrUri: 'redis://redis-main.internal:6379/1?namespace=vat_lookup_cache:'
  },
  {
    id: 'req-prov-1083',
    offeringId: 'offering-postgres-schema',
    offeringName: 'PostgreSQL Isolated Domain Schema',
    requestedBy: 'Marcus Thorne',
    requestingTeam: 'Billing, Growth & Monetization',
    environment: 'PRODUCTION',
    inputs: { schemaName: 'merchant_invoicing_v1', ownerTeam: 'Billing', dataClassification: 'RESTRICTED_PII' },
    state: 'APPROVED',
    dryRunPlan: [
      'CREATE SCHEMA IF NOT EXISTS merchant_invoicing_v1;',
      'GRANT USAGE, SELECT, INSERT, UPDATE ON ALL TABLES TO billing_app_user;',
      'Configure pgcrypto encryption extension for PII columns'
    ],
    createdAt: '2026-08-25 08:30:00',
    approvalDetails: {
      approver: 'Alexandre Renard (Principal Platform SRE)',
      approvedAt: '2026-08-25 09:00:00',
      comments: 'Approved. Fits within Hostinger NVMe storage quota.'
    }
  }
];

// 7. Architecture Decision Records (ADRs)
export const MOCK_ADRS: ArchitectureDecisionRecord[] = [
  {
    id: 'ADR-PLAT-001',
    title: 'Enterprise Architecture Operating System & C4 Model',
    context: 'YamooPay requires a comprehensive, unified enterprise architecture governance framework to balance rapid multi-country expansion with financial-grade precision and zero unplanned outages.',
    decision: 'Adopt a structured C4 architecture model (Context, Containers, Components, Code) integrated directly with a declarative Software Catalog (catalog-as-code) and automated Scorecards.',
    alternativesConsidered: ['Ad-hoc wiki diagrams', 'Heavyweight TOGAF / Mega-enterprise tool suite', 'Pure code without architectural catalog'],
    consequences: {
      positive: ['Instant visibility into component ownership, dependencies, and blast radius', 'Eliminates tribal knowledge in incident triage'],
      negative: ['Requires continuous CI enforcement of catalog.yaml freshness'],
      neutral: ['Requires developers to maintain metadata close to repository code']
    },
    status: 'ACCEPTED',
    author: 'Alexandre Renard (Chief Platform Architect)',
    reviewers: ['Dr. Evelyn Vance', 'Kavita Sundaram', 'Sébastien Mercier'],
    date: '2025-01-15',
    relatedSystems: ['All YamooPay Systems', 'Internal Developer Platform']
  },
  {
    id: 'ADR-PLAT-002',
    title: 'Modular Monolith First Strategy on Docker / Hostinger VPS',
    context: 'Premature microservices migration would create unsustainable operational overhead, network latency, distributed transaction complexity, and excessive VPS costs for the current engineering team scale.',
    decision: 'Maintain a clean, strictly bounded Modular Monolith running inside optimized Docker containers on Hostinger VPS managed via Dokploy, with strict in-process boundaries and explicit module exports.',
    alternativesConsidered: ['Decompose into 35+ microservices with Kubernetes', 'Serverless AWS Lambda / Cloudflare Workers exclusively'],
    consequences: {
      positive: ['Zero network serialization overhead for internal calculations', 'Ultra-low infrastructure cost (~$480/mo total on Hostinger)', 'Single unified CI pipeline and fast local dev loop'],
      negative: ['Requires rigorous linting and architecture tests to prevent leaky module imports'],
      neutral: ['Microservice extraction permitted only when proven by independent scaling or security isolation needs (see ADR-PLAT-003)']
    },
    status: 'ACCEPTED',
    author: 'Alexandre Renard (Chief Platform Architect)',
    reviewers: ['CTO', 'VP Engineering', 'Principal SRE'],
    date: '2025-01-20',
    relatedSystems: ['Calculators & Tax Domain', 'Identity Module', 'Monetization & Billing']
  },
  {
    id: 'ADR-PLAT-003',
    title: 'Strict Criteria & Governance for Microservice Extraction',
    context: 'To prevent unnecessary architectural churn, we need an objective, data-backed gate before extracting any internal domain into a separate deployable microservice.',
    decision: 'Extraction is permitted ONLY if at least one hard trigger is met: (1) Independent compute scaling need > 10x differential, (2) Strict hardware/regulatory security isolation (e.g. PCI HSM), (3) Autonomous deploy cadence required by a dedicated sub-team > 15 engineers.',
    alternativesConsidered: ['Extract on team preference', 'Never extract under any circumstances'],
    consequences: {
      positive: ['Guards against microservice sprawl and unnecessary network boundaries'],
      negative: ['Requires formal ADR justification before creating a new runtime service'],
      neutral: ['Maintains simplicity while keeping a clear path for future scale']
    },
    status: 'ACCEPTED',
    author: 'Alexandre Renard',
    reviewers: ['CTO', 'Dr. Evelyn Vance'],
    date: '2025-02-01',
    relatedSystems: ['Enterprise Architecture Model', 'Software Catalog']
  },
  {
    id: 'ADR-PLAT-006',
    title: 'Developer Portal Strategy: Custom Lightweight IDP vs Backstage',
    context: 'Evaluating Spotify Backstage vs a custom lightweight TypeScript Developer Portal integrated directly into YamooPay.',
    decision: 'Build and operate a custom, fast, lightweight YamooPay Engineering Portal integrated directly with the monorepo and Dokploy rather than running a heavy standalone Backstage instance. Re-evaluate if engineering headcount exceeds 75.',
    alternativesConsidered: ['Adopt Spotify Backstage immediately', 'Rely on GitHub READMEs alone'],
    consequences: {
      positive: ['Zero additional VPS memory footprint (<50MB vs >1.5GB for Backstage Node backend)', 'Immediate seamless integration with YamooPay UI/UX and auth'],
      negative: ['Cannot directly plug in proprietary third-party Backstage plugins without custom adapters'],
      neutral: ['Catalog-as-code schema remains 100% compliant with standard entity descriptors']
    },
    status: 'ACCEPTED',
    author: 'Sébastien Mercier',
    reviewers: ['Alexandre Renard', 'Marcus Thorne'],
    date: '2025-02-15',
    relatedSystems: ['Developer Portal', 'Software Catalog']
  },
  {
    id: 'ADR-PLAT-026',
    title: 'FinOps Architecture & Technology Unit Cost Allocation',
    context: 'Technology costs across Hostinger VPS, Gemini AI tokens, ECB/financial data providers, and Stripe must be directly attributed to business systems and calculated on a per-unit basis.',
    decision: 'Implement an automated FinOps tracking engine calculating cost per 1,000 calculations, cost per active user, and cost per AI interaction with automatic anomaly detection (+25% spike triggers alert).',
    alternativesConsidered: ['Review invoices quarterly in spreadsheets', 'Generic cloud cost dashboard'],
    consequences: {
      positive: ['Real-time visibility into marginal cost of new product features', 'Prevents unexpected billing runaway from AI or provider rate spikes'],
      negative: ['Requires maintaining cost allocation tags on all infrastructure resources'],
      neutral: ['Informs pricing and enterprise tier margins']
    },
    status: 'ACCEPTED',
    author: 'Alexandre Renard (FinOps Lead)',
    reviewers: ['CTO', 'Head of Finance'],
    date: '2025-04-10',
    relatedSystems: ['FinOps Engine', 'CTO Command Center']
  }
];

// 8. Technology Radar
export const MOCK_TECH_RADAR: TechnologyRadarItem[] = [
  {
    id: 'radar-ts',
    technology: 'TypeScript 5.8 (Strict Mode)',
    category: 'LANGUAGES',
    ring: 'ADOPT',
    rationale: 'Primary universal language across client and backend. Strict null checks and zero `any` types enforced in CI.',
    owner: 'Platform Engineering Team',
    approvedUses: 'All frontend components, backend modules, calculations, and workers',
    reviewDate: '2026-12-31',
    alternatives: ['JavaScript', 'Rust (for future high-throughput WASM)']
  },
  {
    id: 'radar-postgres',
    technology: 'PostgreSQL 17 on NVMe',
    category: 'DATA',
    ring: 'ADOPT',
    rationale: 'Rock-solid ACID compliance, pgvector support for AI embeddings, JSONB for flexible snapshots, low operational overhead.',
    owner: 'Platform & SRE Team',
    approvedUses: 'Primary system of record for users, financial logs, and configuration',
    reviewDate: '2026-12-31',
    alternatives: ['MySQL', 'MongoDB', 'Cloud Spanner']
  },
  {
    id: 'radar-dokploy',
    technology: 'Dokploy on Hostinger VPS',
    category: 'INFRASTRUCTURE',
    ring: 'ADOPT',
    rationale: 'Lightweight PaaS built on Docker Compose, zero Kubernetes complexity, automated SSL, rollback, and Git push-to-deploy.',
    owner: 'Platform & SRE Team',
    approvedUses: 'All production container deployments on Hostinger KVM8 VPS',
    reviewDate: '2026-12-31',
    alternatives: ['Coolify', 'Bare Docker Compose', 'Kubernetes (HOLD)']
  },
  {
    id: 'radar-k8s',
    technology: 'Kubernetes (K8s) / Service Mesh',
    category: 'INFRASTRUCTURE',
    ring: 'HOLD',
    rationale: 'Prohibited for current scale. Introduces unnecessary etcd quorum management, YAML overhead, and high RAM usage without business value.',
    owner: 'Alexandre Renard',
    approvedUses: 'None currently. Review trigger: >15 separate deployable services with independent autoscaling needs.',
    restrictions: 'Strictly prohibited from deploying K8s clusters without formal CTO exception',
    reviewDate: '2027-01-01',
    alternatives: ['Dokploy / Docker Compose (ADOPT)']
  },
  {
    id: 'radar-graphql',
    technology: 'GraphQL / Federation',
    category: 'FRAMEWORKS',
    ring: 'HOLD',
    rationale: 'REST with OpenAPI 3.1 contracts and TypeScript SDKs provides cleaner caching, predictable latencies, and lower cognitive load.',
    owner: 'Sébastien Mercier',
    approvedUses: 'None for public APIs. Internal evaluation only.',
    reviewDate: '2026-10-01',
    alternatives: ['REST + OpenAPI 3.1 (ADOPT)', 'tRPC (TRIAL)']
  },
  {
    id: 'radar-opentelemetry',
    technology: 'OpenTelemetry (OTel) Collector',
    category: 'OBSERVABILITY',
    ring: 'ADOPT',
    rationale: 'Vendor-neutral standard for traces, metrics, and logs. Maps service.name directly to Software Catalog IDs.',
    owner: 'Platform SRE',
    approvedUses: 'All HTTP endpoints, background workers, and external integration calls',
    reviewDate: '2026-12-31',
    alternatives: ['Datadog SDK (HOLD)', 'NewRelic Agent (HOLD)']
  },
  {
    id: 'radar-gemini',
    technology: 'Google Gemini 1.5 Pro / Flash via @google/genai',
    category: 'AI',
    ring: 'ADOPT',
    rationale: 'High token efficiency, low latency, structured JSON output, server-side tool calling and grounding.',
    owner: 'AI & Core Engineering',
    approvedUses: 'Financial Copilot, search indexing, support ticket categorization',
    reviewDate: '2026-12-31',
    alternatives: ['OpenAI GPT-4o (TRIAL)', 'Anthropic Claude 3.5 (ASSESS)']
  }
];

// 9. Engineering Standards
export const MOCK_ENGINEERING_STANDARDS: EngineeringStandard[] = [
  {
    id: 'STD-COD-001',
    category: 'CODING',
    title: 'Strict TypeScript & Zero Floating-Point Currency Arithmetic',
    summary: 'All financial calculations must utilize fixed-point integer cents/satoshis or scaled BigInt/Decimal types. Pure float multiplication is rejected in CI.',
    status: 'ACTIVE',
    enforcementMechanism: 'CI_ENFORCED',
    complianceRate: 100,
    ruleReference: 'eslint-plugin-financial-math:no-float-currency'
  },
  {
    id: 'STD-API-002',
    category: 'API',
    title: 'OpenAPI 3.1 & RFC 7807 Problem Details Error Model',
    summary: 'Every public and internal HTTP endpoint must publish a valid OpenAPI 3.1 spec and return standard RFC 7807 JSON errors with a traceable Request-ID.',
    status: 'ACTIVE',
    enforcementMechanism: 'CI_ENFORCED',
    complianceRate: 98.4,
    ruleReference: 'spectral:yamoopay-api-standards'
  },
  {
    id: 'STD-SEC-003',
    category: 'SECURITY',
    title: 'Zero Secrets in Code or Logs & Mandatory Secret Rotation',
    summary: 'Never commit secrets, tokens, or private keys to Git. All secrets managed via environment variables and audited for leaks with Gitleaks.',
    status: 'ACTIVE',
    enforcementMechanism: 'CI_ENFORCED',
    complianceRate: 100,
    ruleReference: 'gitleaks:pre-commit-hook'
  },
  {
    id: 'STD-DOC-004',
    category: 'DOCS',
    title: 'Mandatory Runbook for Every Tier 0/1 Production Component',
    summary: 'Every production service or worker must link an actionable Markdown runbook containing health signals, failure diagnostics, and safe rollback steps.',
    status: 'ACTIVE',
    enforcementMechanism: 'PLATFORM_ENFORCED',
    complianceRate: 95.2,
    ruleReference: 'catalog-linter:require-runbook-tier0'
  }
];

// 10. DORA Metrics
export const MOCK_DORA_METRICS: DORAMetricsSummary = {
  changeLeadTimeHours: 1.8, // Elite (< 1 day)
  deploymentFrequencyPerDay: 4.2, // Elite (> 1/day)
  failedDeploymentRecoveryTimeMinutes: 14.5, // Elite (< 1 hour)
  changeFailRatePercentage: 2.1, // Elite (< 5%)
  deploymentReworkRatePercentage: 1.8, // Elite (< 5%)
  overallDoraTier: 'ELITE',
  historicalTrend: [
    { week: 'W31', leadTimeHours: 2.4, deployments: 22, failRatePct: 3.1 },
    { week: 'W32', leadTimeHours: 2.1, deployments: 26, failRatePct: 2.8 },
    { week: 'W33', leadTimeHours: 1.9, deployments: 29, failRatePct: 2.4 },
    { week: 'W34', leadTimeHours: 1.8, deployments: 31, failRatePct: 2.1 }
  ]
};

// 11. FinOps Costs & Unit Economics
export const MOCK_FINOPS_COSTS: TechnologyCostItem[] = [
  {
    id: 'cost-001',
    provider: 'Hostinger VPS',
    service: 'KVM 8 Dedicated VPS (32GB RAM, 8 vCPU, 400GB NVMe)',
    category: 'INFRASTRUCTURE',
    currency: 'USD',
    amount: 179.99,
    period: 'MONTHLY',
    ownerTeam: 'team-platform-sre',
    allocatedSystem: 'Infrastructure Tier / Dokploy Host',
    environment: 'PRODUCTION',
    allocationMethod: 'DIRECT',
    invoiceReference: 'HST-2026-08-8821'
  },
  {
    id: 'cost-002',
    provider: 'Google Cloud / Gemini API',
    service: 'Gemini 1.5 Flash & Pro Inference Tokens',
    category: 'AI_MODELS',
    currency: 'USD',
    amount: 94.50,
    period: 'USAGE_BASED',
    ownerTeam: 'team-core-fin',
    allocatedSystem: 'AI Financial Assistant & Search',
    environment: 'PRODUCTION',
    allocationMethod: 'MEASURED_USAGE',
    invoiceReference: 'GCP-AI-2026-08'
  },
  {
    id: 'cost-003',
    provider: 'Financial Market Data & Central Banks',
    service: 'ECB, BCEAO & Live Commercial FX Liquidity Feed',
    category: 'DATA_PROVIDERS',
    currency: 'USD',
    amount: 120.00,
    period: 'MONTHLY',
    ownerTeam: 'team-core-fin',
    allocatedSystem: 'Market Data & FX Engine',
    environment: 'PRODUCTION',
    allocationMethod: 'DIRECT',
    invoiceReference: 'FMD-INV-2026-08'
  },
  {
    id: 'cost-004',
    provider: 'Stripe Billing',
    service: 'Billing, Invoicing, Tax calculation & Webhooks',
    category: 'SAAS',
    currency: 'USD',
    amount: 65.20,
    period: 'USAGE_BASED',
    ownerTeam: 'team-billing-growth',
    allocatedSystem: 'Monetization & Subscriptions',
    environment: 'PRODUCTION',
    allocationMethod: 'MEASURED_USAGE',
    invoiceReference: 'STRIPE-2026-08'
  },
  {
    id: 'cost-005',
    provider: 'Hostinger Cloud Backup Storage',
    service: 'Automated Snapshot & Daily WAL PostgreSQL Archive',
    category: 'INFRASTRUCTURE',
    currency: 'USD',
    amount: 25.00,
    period: 'MONTHLY',
    ownerTeam: 'team-platform-sre',
    allocatedSystem: 'PostgreSQL Database Tier',
    environment: 'PRODUCTION',
    allocationMethod: 'DIRECT',
    invoiceReference: 'HST-BKP-2026-08'
  }
];

export const MOCK_UNIT_ECONOMICS: TechnologyUnitEconomics[] = [
  {
    metricName: 'Cost per 1,000 Financial Calculations',
    unitDefinition: 'Salary, Mortgage, Loan & FX Computations',
    totalMonthlyCostUsd: 145.00,
    monthlyUnitVolume: 1250000,
    costPerUnitUsd: 0.000116, // $0.116 per 1,000 calculations
    trendPercentage: -8.4,
    benchmarkTargetUsd: 0.000200
  },
  {
    metricName: 'Cost per Monthly Active User (MAU)',
    unitDefinition: 'Total Tech Spend / Verified MAU',
    totalMonthlyCostUsd: 484.69,
    monthlyUnitVolume: 84200,
    costPerUnitUsd: 0.00575, // $0.0058 / MAU (~$5.75 per 1,000 MAU)
    trendPercentage: -12.1,
    benchmarkTargetUsd: 0.01500
  },
  {
    metricName: 'Cost per AI Copilot Query',
    unitDefinition: 'Gemini Token Spend / Completed Financial AI Queries',
    totalMonthlyCostUsd: 94.50,
    monthlyUnitVolume: 42000,
    costPerUnitUsd: 0.00225, // $0.00225 per AI conversation turn
    trendPercentage: -15.2,
    benchmarkTargetUsd: 0.00500
  }
];

// 12. Technical Debt & Architecture Risk Registers
export const MOCK_TECH_DEBT: TechnicalDebtItem[] = [
  {
    id: 'DEBT-001',
    system: 'Calculators & Tax Domain',
    title: 'Legacy hardcoded Scottish tax bracket table in legacy helper',
    description: 'A legacy 2024 Scottish PAYE bracket helper is still referenced by one deprecated reporting script. Needs unified routing through the versioned 2026 ruleset.',
    category: 'ARCHITECTURE',
    impact: 'MEDIUM',
    effort: 'SMALL',
    interestConsequences: 'Requires dual maintenance if Scottish local council rules update again.',
    ownerTeam: 'team-core-fin',
    targetRemediationDate: '2026-09-15',
    status: 'IN_PROGRESS'
  },
  {
    id: 'DEBT-002',
    system: 'Developer Platform',
    title: 'Manual OpenAPI Spec Export for internal micro-routes',
    description: 'Internal private endpoints require running an npm script to regenerate OpenAPI schema instead of automatic type reflection.',
    category: 'DEPENDENCY',
    impact: 'LOW',
    effort: 'SMALL',
    interestConsequences: 'Adds 3 minutes to developer PR cycle time.',
    ownerTeam: 'team-developer-ecosystem',
    targetRemediationDate: '2026-09-30',
    status: 'OPEN'
  }
];

export const MOCK_ARCHITECTURE_RISKS: ArchitectureRisk[] = [
  {
    id: 'RISK-001',
    title: 'Single Hostinger VPS Node Concentration for All Docker Services',
    system: 'Infrastructure Tier',
    threatScenario: 'A catastrophic hardware fault on the Hostinger KVM8 hypervisor could cause temporary downtime until WAL backup is restored on a cold standby VPS.',
    probability: 'LOW',
    impact: 'HIGH',
    status: 'MITIGATING',
    mitigationPlan: 'Automated 15-minute Dokploy state snapshots + warm standby secondary VPS ready for DNS failover within 8 minutes.',
    owner: 'Alexandre Renard (Principal SRE)',
    acceptedUntil: '2026-11-01'
  },
  {
    id: 'RISK-002',
    title: 'Developer Platform API Key Bus Factor (Single Maintainer)',
    system: 'Developer Platform',
    threatScenario: 'Key developer platform modules have high reliance on one principal architect.',
    probability: 'MEDIUM',
    impact: 'MEDIUM',
    status: 'MITIGATING',
    mitigationPlan: 'Pair programming rotations with Financial Core engineers and complete architecture docs.',
    owner: 'Sébastien Mercier',
    acceptedUntil: '2026-10-01'
  }
];

// 13. Platform Health Indicator
export const MOCK_PLATFORM_HEALTH: PlatformHealthIndicator = {
  serviceCatalogStatus: 'HEALTHY',
  selfServiceProvisioningStatus: 'HEALTHY',
  ciPipelineHealth: 'HEALTHY',
  dokployIntegrationStatus: 'HEALTHY',
  openTelemetryCollectorStatus: 'HEALTHY',
  costDataFreshnessHours: 1.2,
  uptimePercentage30Days: 99.98
};

// Helper: Compute Blast Radius for a given entity
export function computeImpactAnalysis(entityId: string, changeType: ArchitectureImpactAnalysis['changeType']): ArchitectureImpactAnalysis {
  const entity = MOCK_CATALOG_ENTITIES.find(e => e.entityId === entityId);
  const directDependents = MOCK_DEPENDENCIES
    .filter(d => d.targetEntityId === entityId)
    .map(d => d.sourceName);

  const isTier0 = entity?.criticality === 'TIER_0';
  const blastRadiusScore = isTier0 ? 'HIGH' : directDependents.length > 5 ? 'MEDIUM' : 'LOW';

  return {
    targetEntityId: entityId,
    changeType,
    directConsumers: directDependents.length > 0 ? directDependents : ['Universal Calculators UI', 'My YamooPay Dashboard'],
    transitiveConsumers: ['YamooPay Mobile PWA', 'Public Developer API Clients', 'Commercial Partners'],
    affectedCriticalTier0Systems: isTier0 ? ['Financial Calculation Core', 'User Identity Authority'] : [],
    affectedMarkets: ['France (FR)', 'United Kingdom (GB)', 'United States (US)', 'Senegal (SN)', 'Germany (DE)'],
    accountableOwners: [entity?.ownership.technicalLead || 'Alexandre Renard', entity?.ownership.ownerTeamName || 'Platform SRE'],
    blastRadiusScore,
    suggestedAutomatedTests: [
      'vitest run src/tests/financial-regression.test.ts',
      'spectral lint openapi.yaml --ruleset yamoopay-rules',
      'npm run test:e2e-contracts'
    ],
    rollbackStrategy: 'Dokploy 1-click container roll-back to previous tagged image hash in < 30 seconds'
  };
}

// Helper: Generate declarative catalog.yaml for an entity
export function generateCatalogYaml(entity: SoftwareCatalogEntity): string {
  return `# YamooPay Catalog-as-Code (RFC-PLAT-008)
apiVersion: yamoopay.io/v1alpha1
kind: ${entity.kind}
metadata:
  name: ${entity.entityId}
  title: "${entity.name}"
  description: "${entity.description}"
  domain: "${entity.domain}"
  system: "${entity.system}"
  tags:
    - "${entity.lifecycle.toLowerCase()}"
    - "${entity.criticality.toLowerCase()}"
    - "${entity.dataClassification.toLowerCase()}"
spec:
  lifecycle: ${entity.lifecycle}
  criticality: ${entity.criticality}
  owner:
    team: "${entity.ownership.ownerTeamName}"
    techLead: "${entity.ownership.technicalLead}"
    onCall: "${entity.ownership.onCallSchedule}"
    codeowners: "${entity.ownership.codeownersPath}"
  runtime:
    language: "${entity.runtime.language}"
    deploymentUnit: "${entity.runtime.deploymentUnit}"
    environment: "${entity.runtime.environment}"
  slo:
    availability: "${entity.slo.availabilityTarget}"
    latencyP99: "${entity.slo.latencyP99Target}"
    sliMetric: "${entity.slo.sliMetricName}"
  documentation:
    readme: "${entity.documentation.readmeUrl}"
    runbook: "${entity.documentation.runbookUrl}"
    lastReviewed: "${entity.documentation.lastReviewedAt}"
  finops:
    costCenter: "${entity.costCenter}"
    allocatedMonthlyUsd: ${entity.allocatedMonthlyCostUsd}
`;
}
